import loadable from '@loadable/component';
import { _ as _defineProperty } from './defineProperty-377d5a7f.js';
import { useContext, createContext, useState, useRef, useCallback, useEffect } from 'react';
import { Text, TextElementTypes } from '@picsart/text';
import { useTranslator } from '@picsart/rc/services/localization';
import { FontWeights } from '@picsart/cascade/fonts';
import { ScreenSizes } from '@picsart/cascade/responsive';
import { Typography } from '@picsart/cascade/typography';
import { _ as _slicedToArray } from './slicedToArray-b4278ecd.js';
import noop from '@picsart/rc/noop';
import { jsx, jsxs } from 'react/jsx-runtime';
import { usePulseSettings } from '@pulse/react';
import { setStorage } from './services/browserStorage/index.js';
import { Button, ButtonSkinPrimary, ButtonSizeLG, ButtonVariants, ButtonElementTypes } from '@picsart/button';
import IconChevronLeftLarge from '@picsart/icons/IconChevronLeftLarge';
import useIsomorphicLayoutEffect from './useIsomorphicLayoutEffect/index.js';
import { createUseStyles } from 'react-jss';

var defaultState = {
  // Inner Props
  email: '',
  username: '',
  password: '',
  action: AuthActionOptions === null || AuthActionOptions === void 0 ? void 0 : AuthActionOptions.SignIn,
  hasEmailConsent: false,
  currentPage: ComponentsOptions === null || ComponentsOptions === void 0 ? void 0 : ComponentsOptions.OptionsForm,
  geoZone: '',
  checkedStates: {},
  socialData: {
    provider: '',
    credential: ''
  },
  status: {
    type: StatusOptions === null || StatusOptions === void 0 ? void 0 : StatusOptions.Default,
    message: ''
  },
  // Setters
  setEmail: noop,
  setUsername: noop,
  setPassword: noop,
  setStatus: noop,
  setAuthAction: noop,
  setHasEmailConsent: noop,
  setGeoZone: noop,
  setCurrentPage: noop,
  setCheckedStates: noop,
  setSocialData: noop
};

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var RegistrationFormContext = /*#__PURE__*/createContext(defaultState);
var useRegistrationFormContext = function useRegistrationFormContext() {
  return useContext(RegistrationFormContext);
};
function RegistrationFormProvider(_ref) {
  var children = _ref.children;
  var _useState = useState(ComponentsOptions.OptionsForm),
    _useState2 = _slicedToArray(_useState, 2),
    currentPage = _useState2[0],
    setCurrentPage = _useState2[1];
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    email = _useState4[0],
    setEmail = _useState4[1];
  var _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    username = _useState6[0],
    setUsername = _useState6[1];
  var _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    password = _useState8[0],
    setPassword = _useState8[1];
  var _useState9 = useState(''),
    _useState10 = _slicedToArray(_useState9, 2),
    geoZone = _useState10[0],
    setGeoZone = _useState10[1];
  var _useState11 = useState(AuthActionOptions.SignIn),
    _useState12 = _slicedToArray(_useState11, 2),
    action = _useState12[0],
    setAuthAction = _useState12[1];
  var _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    hasEmailConsent = _useState14[0],
    setHasEmailConsent = _useState14[1];
  var _useState15 = useState({}),
    _useState16 = _slicedToArray(_useState15, 2),
    checkedStates = _useState16[0],
    setCheckedStates = _useState16[1];
  var _useState17 = useState({
      provider: '',
      credential: ''
    }),
    _useState18 = _slicedToArray(_useState17, 2),
    socialData = _useState18[0],
    setSocialData = _useState18[1];
  var _useState19 = useState({
      type: StatusOptions.Default,
      message: ''
    }),
    _useState20 = _slicedToArray(_useState19, 2),
    status = _useState20[0],
    setStatus = _useState20[1];
  return /*#__PURE__*/jsx(RegistrationFormContext.Provider, {
    value: _objectSpread(_objectSpread({}, defaultState), {}, {
      email: email,
      username: username,
      password: password,
      currentPage: currentPage,
      geoZone: geoZone,
      action: action,
      hasEmailConsent: hasEmailConsent,
      status: status,
      checkedStates: checkedStates,
      socialData: socialData,
      setEmail: setEmail,
      setUsername: setUsername,
      setAuthAction: setAuthAction,
      setHasEmailConsent: setHasEmailConsent,
      setGeoZone: setGeoZone,
      setPassword: setPassword,
      setCurrentPage: setCurrentPage,
      setStatus: setStatus,
      setCheckedStates: setCheckedStates,
      setSocialData: setSocialData
    }),
    children: children
  });
}

var registrationData = {
  optionsForm: {
    title: 'Before you jump in...',
    description: 'Create an account to save your work. Or sign in with email or username if you already have a Picsart account.',
    authOptions: {
      email: 'Continue with email | username'
    }
  },
  modalVisuals: {
    modalImage: {
      url: 'https://pastatic.picsart.com/cms-pastatic/56e5f8b2-343c-4b3e-93cd-96be7a21ca9c.svg',
      alt: 'girl and effects'
    },
    reversed: false
  },
  emailForm: {
    title: 'Welcome!',
    description: 'Create your free account with email. Already have a Picsart account? Log in with your email or username.',
    placeholder: 'Email | username',
    label: 'Your email | username',
    wrongEmail: 'That email doesn’t seem quite right. Check the format and try again.'
  },
  createPasswordForm: {
    title: 'Welcome to Picsart',
    description: 'Create your account using',
    placeholder: 'Unique password',
    label: 'Create a password',
    checkBoxText: 'I want marketing content such as the latest Picsart news and special offers.'
  },
  insertPasswordForm: {
    title: 'Welcome back',
    description: 'Log in to your Picsart account',
    wrongPassword: 'Looks like that\'s the wrong password',
    label: 'Enter your password',
    placeholder: 'Password',
    trouble: 'Trouble logging in?'
  },
  noInternetForm: {
    title: 'No internet connection',
    description: 'Maybe the stars aren’t aligned or maybe it’s a poor signal. Either way, your account registration or log in isn’t finished yet. Check your connection and try again.'
  }
};
var useAuthModalData = function useAuthModalData(form) {
  var _usePulseSettings = usePulseSettings({
      name: 'registration_modal_json',
      tag: 'registration_modal_json'
    }),
    settings = _usePulseSettings.settings;
  return (settings === null || settings === void 0 ? void 0 : settings[form]) || registrationData[form];
};

/// new google JS API require google button rendering

/// google button "shape" attribute values
var googleButtonShape = /*#__PURE__*/function (googleButtonShape) {
  googleButtonShape["rectangular"] = "rectangular";
  googleButtonShape["pill"] = "pill";
  googleButtonShape["circle"] = "circle";
  googleButtonShape["square"] = "square";
  return googleButtonShape;
}({});

/// google button "text" attribute values
var googleButtonLabelText = /*#__PURE__*/function (googleButtonLabelText) {
  googleButtonLabelText["signin_with"] = "signin_with";
  googleButtonLabelText["signup_with"] = "signup_with";
  googleButtonLabelText["continue_with"] = "continue_with";
  googleButtonLabelText["signin"] = "signin";
  return googleButtonLabelText;
}({});

/// google button "size" attribute values
var googleButtonSize = /*#__PURE__*/function (googleButtonSize) {
  googleButtonSize["large"] = "large";
  googleButtonSize["medium"] = "medium";
  googleButtonSize["small"] = "small";
  return googleButtonSize;
}({});

/// google button "theme" attribute values
var googleButtonTheme = /*#__PURE__*/function (googleButtonTheme) {
  googleButtonTheme["outline"] = "outline";
  googleButtonTheme["filled_blue"] = "filled_blue";
  googleButtonTheme["filled_black"] = "filled_black";
  return googleButtonTheme;
}({});
var AuthModalDataForms = /*#__PURE__*/function (AuthModalDataForms) {
  AuthModalDataForms["insertPasswordForm"] = "insertPasswordForm";
  AuthModalDataForms["createPasswordForm"] = "createPasswordForm";
  AuthModalDataForms["noInternetForm"] = "noInternetForm";
  AuthModalDataForms["modalVisuals"] = "modalVisuals";
  AuthModalDataForms["optionsForm"] = "optionsForm";
  AuthModalDataForms["emailForm"] = "emailForm";
  return AuthModalDataForms;
}({});

var useStyles$1 = createUseStyles(function (_ref) {
  var spacings = _ref.spacings;
    _ref.colors;
  return {
    root: {
      display: 'grid',
      gap: spacings.s8,
      gridTemplateColumns: "".concat(spacings.s40, " 1fr"),
      alignContent: 'end',
      paddingTop: spacings.s6,
      width: '100%'
    }
  };
}, {
  name: 'registration-footer'
});

function Footer(_ref) {
  var clickHandler = _ref.clickHandler,
    label = _ref.label,
    buttonDisabled = _ref.buttonDisabled,
    backHandler = _ref.backHandler,
    _ref$buttonType = _ref.buttonType,
    buttonType = _ref$buttonType === void 0 ? 'button' : _ref$buttonType;
  var _useStyles = useStyles$1(),
    root = _useStyles.root;
  var translate = useTranslator();
  var submitButton = useRef(null);
  useIsomorphicLayoutEffect(function () {
    document.addEventListener('keydown', function (e) {
      if (submitButton.current !== null && e.key === 'Enter') {
        e.preventDefault();
        submitButton.current.click();
      }
    });
  }, []);
  return /*#__PURE__*/jsxs("div", {
    className: root,
    "data-testid": "registration-popup-footer",
    "data-pulse-section-group": "continue with email",
    children: [/*#__PURE__*/jsx(Button, {
      "data-pulse-name": "reg-back-button",
      "data-testid": "reg-back-button",
      onClick: backHandler,
      centerIcon: IconChevronLeftLarge,
      skin: ButtonSkinPrimary,
      size: ButtonSizeLG,
      variant: ButtonVariants.Outline
    }), /*#__PURE__*/jsx(Button, {
      "data-pulse-name": label,
      "data-testid": "reg-continue-button",
      tag: ButtonElementTypes.Button,
      isSubmitButton: buttonType === 'submit',
      onClick: clickHandler,
      isDisabled: buttonDisabled,
      ref: submitButton,
      size: ButtonSizeLG,
      children: translate(label)
    })]
  });
}

var useStyles = createUseStyles(function (_ref) {
  var spacings = _ref.spacings;
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      height: '100%'
    },
    formContent: {
      display: 'grid',
      gap: spacings.s16
    },
    passwordField: {
      width: '100%'
    },
    passwordError: {
      height: 8
    }
  };
}, {
  name: 'insert-password-form'
});

function NoInternet() {
  var _useStyles = useStyles(),
    formContent = _useStyles.formContent,
    root = _useStyles.root;
  var _useAuthModalData = useAuthModalData(AuthModalDataForms.noInternetForm),
    title = _useAuthModalData.title,
    description = _useAuthModalData.description;
  var _useRegistrationFormC = useRegistrationFormContext(),
    setCurrentPage = _useRegistrationFormC.setCurrentPage,
    setStatus = _useRegistrationFormC.setStatus;
  var translate = useTranslator();

  // Back button handler
  var handleClick = useCallback(function (e) {
    e.preventDefault();
    setStatus({
      type: StatusOptions.Default,
      message: ''
    });
    setCurrentPage(ComponentsOptions.EmailForm);
  }, [setCurrentPage, setStatus]);

  // Setting analytics screen_name for firing skip event
  useEffect(function () {
    setStorage(ANALYTICS_SCREEN_NAME_KEY, AnalyticsSkipScreenOptions.Connection, false);
  }, []);
  return /*#__PURE__*/jsxs("div", {
    className: root,
    "data-testid": "no-internet",
    children: [/*#__PURE__*/jsxs("div", {
      className: formContent,
      children: [/*#__PURE__*/jsx(Text, {
        size: _defineProperty(_defineProperty({
          Default: Typography.t8
        }, ScreenSizes.XL, Typography.t8), ScreenSizes.MD, Typography.t7),
        weight: FontWeights.SemiBold,
        elementType: TextElementTypes.H3,
        children: translate(title)
      }), /*#__PURE__*/jsx(Text, {
        size: Typography.t5,
        children: translate(description)
      })]
    }), /*#__PURE__*/jsx(Footer, {
      backHandler: function backHandler(e) {
        return handleClick(e);
      },
      clickHandler: function clickHandler(e) {
        return handleClick(e);
      },
      label: "Retry"
    })]
  });
}

// This is a map of each page of Registration form
var ComponentsMap = {
  OptionsForm: loadable(function () {
    return import('./index-285a2b9d.js');
  }),
  EmailForm: loadable(function () {
    return import('./index-016a03e5.js');
  }),
  CreatePasswordForm: loadable(function () {
    return import('./index-69a753f4.js');
  }),
  InsertPasswordForm: loadable(function () {
    return import('./index-0aa167c3.js');
  }),
  AccountTermination: loadable(function () {
    return import('./index-90755199.js');
  }),
  ToGetStarted: loadable(function () {
    return import('./index-e64b5850.js');
  }),
  NoInternet: NoInternet
};
var ComponentsOptions = /*#__PURE__*/function (ComponentsOptions) {
  ComponentsOptions["OptionsForm"] = "OptionsForm";
  ComponentsOptions["EmailForm"] = "EmailForm";
  ComponentsOptions["CreatePasswordForm"] = "CreatePasswordForm";
  ComponentsOptions["InsertPasswordForm"] = "InsertPasswordForm";
  ComponentsOptions["AccountTermination"] = "AccountTermination";
  ComponentsOptions["ToGetStarted"] = "ToGetStarted";
  ComponentsOptions["NoInternet"] = "NoInternet";
  return ComponentsOptions;
}({});

// Analytics related enums
var AnalyticsScreenNameOptions = /*#__PURE__*/function (AnalyticsScreenNameOptions) {
  AnalyticsScreenNameOptions["OptionsForm"] = "general";
  AnalyticsScreenNameOptions["EmailForm"] = "email_flow";
  AnalyticsScreenNameOptions["CreatePasswordForm"] = "new";
  AnalyticsScreenNameOptions["InsertPasswordForm"] = "returning";
  AnalyticsScreenNameOptions["NoInternet"] = "connection_lost";
  AnalyticsScreenNameOptions["ToGetStarted"] = "consent_screen";
  return AnalyticsScreenNameOptions;
}({});
var AnalyticsActionOptions = /*#__PURE__*/function (AnalyticsActionOptions) {
  AnalyticsActionOptions["SignIn"] = "sign_in";
  AnalyticsActionOptions["SignUp"] = "sign_up";
  AnalyticsActionOptions["Skip"] = "skip";
  return AnalyticsActionOptions;
}({});
var RegistrationActionOptions = /*#__PURE__*/function (RegistrationActionOptions) {
  RegistrationActionOptions["SignUp"] = "sign_up";
  RegistrationActionOptions["EmptyString"] = "";
  RegistrationActionOptions["Combined"] = "combined";
  return RegistrationActionOptions;
}({});
var ANALYTICS_SCREEN_NAME_KEY = 'reg-analytics-screen';
var ANALYTICS_CONSENTS_KEY = 'reg-analytics-screen';
var AnalyticsSkipScreenOptions = /*#__PURE__*/function (AnalyticsSkipScreenOptions) {
  AnalyticsSkipScreenOptions["General"] = "general";
  AnalyticsSkipScreenOptions["EmailFlow"] = "email_flow";
  AnalyticsSkipScreenOptions["New"] = "new";
  AnalyticsSkipScreenOptions["Returning"] = "returning";
  AnalyticsSkipScreenOptions["Connection"] = "connection";
  AnalyticsSkipScreenOptions["Termination"] = "account_termination";
  AnalyticsSkipScreenOptions["ToGetStarted"] = "consent_screen";
  return AnalyticsSkipScreenOptions;
}({});
var AnalyticsSocialItemOptions = /*#__PURE__*/function (AnalyticsSocialItemOptions) {
  AnalyticsSocialItemOptions["Google"] = "reg_select_google";
  AnalyticsSocialItemOptions["Apple"] = "reg_select_apple";
  AnalyticsSocialItemOptions["Facebook"] = "reg_select_fb";
  return AnalyticsSocialItemOptions;
}({});

// Actions
var AuthActionOptions = /*#__PURE__*/function (AuthActionOptions) {
  AuthActionOptions["SignIn"] = "signIn";
  AuthActionOptions["SignUp"] = "signup";
  return AuthActionOptions;
}({});

// Regular Expressions
var RegexpOptions = /*#__PURE__*/function (RegexpOptions) {
  RegexpOptions["ValidEmailAddress"] = "(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|\"(?:[\x01-\b\x0B\f\x0E-\x1F!#-[]-\x7F]|\\\\[\x01-\t\x0B\f\x0E-\x7F])*\")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\b\x0B\f\x0E-\x1F!-ZS-\x7F]|\\\\[\x01-\t\x0B\f\x0E-\x7F])+)\\])";
  RegexpOptions["ExpectedLength"] = "^.{8,16}$";
  RegexpOptions["AtLeastOneCharacter"] = "[a-zA-Z]";
  RegexpOptions["AtLeastOneNumber"] = ".*[0-9].*";
  return RegexpOptions;
}({});

// statuses
var StatusOptions = /*#__PURE__*/function (StatusOptions) {
  StatusOptions["Success"] = "Success";
  StatusOptions["Warning"] = "Warning";
  StatusOptions["Error"] = "Error";
  StatusOptions["Default"] = "Default";
  StatusOptions["TextFieldStatuses"] = "TextFieldStatuses";
  return StatusOptions;
}({});
var RegErrorReasons = /*#__PURE__*/function (RegErrorReasons) {
  RegErrorReasons["Consents"] = "consents_not_provided";
  return RegErrorReasons;
}({});

// Geo Zones / Locations
var RestrictedGeoZones = /*#__PURE__*/function (RestrictedGeoZones) {
  RestrictedGeoZones["Russia"] = "RU";
  RestrictedGeoZones["Japan"] = "JP";
  RestrictedGeoZones["SouthKorea"] = "KR";
  return RestrictedGeoZones;
}({});
var AUTH_BUTTON_TEXT_B_VARIANT = 'Log in';
var AUTH_BUTTON_TEXT = 'Log in or sign up';

// Magic Link URL
var MAGIC_LINK_URL = '/forgot-password?isMagicLink=1';

// Spcial Button Scripts
var FACEBOOK_PLATFORM_SCRIPT_URL = 'https://connect.facebook.net/en_US/sdk.js';
var GOOGLE_PLATFORM_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
var SocialProviderOptions = /*#__PURE__*/function (SocialProviderOptions) {
  SocialProviderOptions["google"] = "google";
  SocialProviderOptions["apple"] = "apple";
  SocialProviderOptions["facebook"] = "facebook";
  return SocialProviderOptions;
}({});

export { ANALYTICS_SCREEN_NAME_KEY as A, ComponentsOptions as C, FACEBOOK_PLATFORM_SCRIPT_URL as F, GOOGLE_PLATFORM_SCRIPT_URL as G, MAGIC_LINK_URL as M, RestrictedGeoZones as R, StatusOptions as S, ANALYTICS_CONSENTS_KEY as a, AuthActionOptions as b, AnalyticsActionOptions as c, SocialProviderOptions as d, RegErrorReasons as e, AuthModalDataForms as f, AUTH_BUTTON_TEXT as g, AUTH_BUTTON_TEXT_B_VARIANT as h, useRegistrationFormContext as i, AnalyticsSocialItemOptions as j, ComponentsMap as k, RegistrationFormProvider as l, defaultState as m, AnalyticsSkipScreenOptions as n, googleButtonTheme as o, googleButtonSize as p, googleButtonLabelText as q, googleButtonShape as r, AnalyticsScreenNameOptions as s, RegistrationActionOptions as t, useAuthModalData as u, RegexpOptions as v, Footer as w };
//# sourceMappingURL=constants-d756cbb3.js.map
